/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { toMechanicHotspot } from "@/abilities/mechanic/features/hotspots/models/mechanic-hotspot.model";
import {
    QDataSource,
    QIT_PREVIEW_IMAGE_COLUMN,
    QIT_PREVIEW_IMAGE_ROW_FIELD,
    QTableRowType,
} from "@/shared/components/table/q-data-source";
import { getDataDisplayValue } from "@/shared/data-display-config/composable/data-display.helper";
import { PRODUCT_TITLE_QUERY } from "@/shared/graphql/product-title.query";
import { Product } from "@/shared/services/graphql/generated/consumer-graph-types";
import { OperationVariables } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import { get, set } from "@vueuse/core";
import { QTableProps } from "quasar";
import { computed, ComputedRef, ref, Ref, watch } from "vue";
import { usePartslistFiles } from "../helpers/assembly-files-helper";
import { MechanicsListQueryParams, useMechanicsListQuery } from "../helpers/mechanics-list-result";
import { useMechanicStore } from "../store/mechanic.store";

export const MECHANIC_OBJECT_KEY = "mechanicObject";

//TODO diese Query hat in der Mechanik eigentlich nix zu suchen. Ist Teil der Product-Ability
export function useProductDescriptionQuery(productFilterVariables: OperationVariables) {
    const {
        result,
        loading: productDescriptionLoading,
        onError,
    } = useQuery(PRODUCT_TITLE_QUERY, productFilterVariables);
    const product: ComputedRef<Product> = computed(() => {
        return result.value?.product ?? undefined;
    });

    onError((error) => {
        console.error(error);
    });

    const productTitle = computed(() => {
        return product.value?.localizations?.title ?? "";
    });

    return {
        productTitle,
        productDescriptionLoading,
    };
}

export function useMechanicModel(params: Ref<MechanicsListQueryParams>) {
    const {
        dataDisplayFields,
        priorityDataDisplayFields,
        error: partListError,
        data,
        loading: partListLoading,
    } = useMechanicsListQuery(params);

    const columns = computed(() => {
        if (dataDisplayFields.value) {
            const result = dataDisplayFields.value?.map((displayField) => {
                return {
                    name: displayField.key,
                    field: displayField.key,
                    label: displayField.title,
                    align: "left",
                };
            });

            return [
                {
                    name: QIT_PREVIEW_IMAGE_COLUMN,
                    field: QIT_PREVIEW_IMAGE_ROW_FIELD,
                    label: "",
                },
                ...result,
                {
                    name: "click-arrow",
                    field: "click-arrow",
                    label: "",
                },
            ];
        }

        return undefined;
    });

    const mobileColumns = computed(() => {
        const result = priorityDataDisplayFields.value.map((displayField) => {
            return {
                name: displayField.key,
                field: displayField.key,
                label: displayField.title,
                align: "left",
            };
        });

        return [
            ...result,
            {
                name: "click-arrow",
                field: "click-arrow",
                label: "",
                align: "center",
            },
        ];
    });

    const mechanicStore = useMechanicStore();
    mechanicStore.clearHotspots();
    const rows = ref<undefined | any[]>(undefined);
    const visibleColumns = ref<string[]>(["click-arrow", QIT_PREVIEW_IMAGE_COLUMN]);

    watch([dataDisplayFields, data], () => {
        if (data.value && dataDisplayFields.value) {
            mechanicStore.setHotspots(
                get(data).map((node) => toMechanicHotspot(node as { id: string } & Record<string, unknown>))
            );
            const newRows = data.value
                .filter((node) => node !== null || node !== undefined)
                .map((node) => {
                    const result: Partial<QTableRowType> = {
                        //always add id and title as string field as in QTableRowType
                        id: node.id,
                        title: node.teasers?.title ?? node.localizations?.title ?? node.id,
                        //always add assemblyId as string field for navigation
                        assemblyId: node.assemblyId,
                        isAssembly: node.isAssembly,
                        [MECHANIC_OBJECT_KEY]: node,
                        [QIT_PREVIEW_IMAGE_ROW_FIELD]: node.previewImage?.url,
                    };
                    dataDisplayFields.value?.forEach((dataDisplayField) => {
                        const dataValue = getDataDisplayValue(dataDisplayField, node);
                        if (
                            dataDisplayField.key === "id" ||
                            dataDisplayField.key === "assemblyId" ||
                            dataDisplayField.key === "isAssembly" ||
                            dataDisplayField.key === "title"
                        ) {
                            if (dataValue.exists && !visibleColumns.value.includes(dataDisplayField.key)) {
                                visibleColumns.value.push(dataDisplayField.key);
                            }
                        } else {
                            // Store as object with formattedValue and optional symbols
                            result[dataDisplayField.key] = {
                                formattedValue: dataValue.formattedValue,
                                symbols: dataValue.symbols,
                                colorClass: dataValue.colorClass,
                            };
                            if (dataValue.exists && !visibleColumns.value.includes(dataDisplayField.key)) {
                                visibleColumns.value.push(dataDisplayField.key);
                            }
                        }
                    });
                    return result;
                });
            set(rows, newRows);
        }
    });

    const qDataSource: ComputedRef<QDataSource> = computed(() => {
        return {
            columns: columns.value as QTableProps["columns"],
            mobileColumns: mobileColumns.value as QTableProps["columns"],
            rows: rows.value,
            visibleColumns: visibleColumns.value,
        };
    });

    const {
        loading: imageLoading,
        image2D: images2D,
        has2DImages,
        image3D: images3D,
        has3DImages,
        error: imageError,
    } = usePartslistFiles(
        computed(() => params.value.assemblyId),
        computed(() => params.value.productId),
        computed(() => params.value.assetId)
    );

    const loading = computed(() => {
        return partListLoading.value || imageLoading.value;
    });

    const error = computed(() => {
        return partListError.value || imageError.value;
    });

    watch(partListLoading, () => {
        mechanicStore.setHotspotLoading(partListLoading.value);
    });

    return {
        loading,
        imageLoading,
        partListLoading,
        images2D,
        has2DImages,
        images3D,
        has3DImages,
        error,
        qDataSource,
    };
}
